import React, { useLayoutEffect, useState } from 'react';

export const MOSTLY_OPAQUE_PROGRESS = 0.8;

/** Longest transition (duration + delay) on an element, in ms; 0 when it changes instantly. */
const longestTransitionMs = (element: HTMLElement) => {
  const style = window.getComputedStyle(element);
  const toMs = (value: string) => (value.trim().endsWith('ms') ? parseFloat(value) : parseFloat(value) * 1000) || 0;
  const durations = (style.transitionDuration || '').split(',').map(toMs);
  const delays = (style.transitionDelay || '').split(',').map(toMs);
  return durations.reduce((longest, duration, index) => Math.max(longest, duration + (delays[index] ?? delays[0] ?? 0)), 0);
};

/**
 * True once the visible backdrop has (almost) finished its entrance fade, immediately
 * when it has none (handoffs, retained-frame takeovers, reduced motion). Anything
 * behind it is effectively covered only then.
 */
export const useOpaqueOnceEntered = (ref: React.RefObject<HTMLElement>, visible: boolean) => {
  const [opaque, setOpaque] = useState(false);
  useLayoutEffect(() => {
    const element = ref.current;
    if (!visible || !element) {
      setOpaque(false);
      return undefined;
    }
    const wait = longestTransitionMs(element);
    if (wait === 0) {
      setOpaque(true);
      return undefined;
    }
    setOpaque(false);
    const finish = () => setOpaque(true);
    const ended = (event: TransitionEvent) => {
      if (event.target === element && event.propertyName === 'opacity') finish();
    };
    element.addEventListener('transitionend', ended);
    // An ease-out fade is about 94% opaque 80% of the way through, so it covers from then.
    // The timer also covers transitionend never firing (interrupted fade, hidden tab).
    const fallback = window.setTimeout(finish, wait * MOSTLY_OPAQUE_PROGRESS);
    return () => {
      element.removeEventListener('transitionend', ended);
      window.clearTimeout(fallback);
    };
  }, [ref, visible]);
  return opaque;
};
